export interface SupplierContributionRow {
  supplierId: number | null;
  name: string;
  units: number | null;
  unitsAfter?: number | null;
  volumePct: number | null;
  volumePctAfter?: number | null;
  weightPct: number | null;
  weightPctAfter?: number | null;
  amount: number | null;
  amountAfter?: number | null;
  /** Importe mínimo de pedido del proveedor (regla de compra), si lo tiene. */
  minAmount?: number | null;
  /** Si tras el ajuste el proveedor llega a su importe mínimo. */
  minMet?: boolean | null;
}

interface SupplyVehicleSuppliersPanelProps {
  rows: SupplierContributionRow[];
  t: (key: string, options?: Record<string, unknown>) => string;
}

function formatNumber(value: number | null | undefined, maximumFractionDigits = 0): string {
  return value == null || !Number.isFinite(value) ? '—' : value.toLocaleString(undefined, { maximumFractionDigits });
}

function formatPct(value: number | null | undefined): string {
  return value == null || !Number.isFinite(value) ? '—' : `${formatNumber(value, 1)} %`;
}

function formatMoney(value: number | null | undefined): string {
  return value == null || !Number.isFinite(value) ? '—' : `${formatNumber(value, 0)} €`;
}

/** «antes → después» cuando hay un ajuste propuesto; solo el valor actual si no. */
function change(before: string, after: string | null): string {
  return after == null || after === before ? before : `${before} → ${after}`;
}

function sumKnown(values: Array<number | null | undefined>): number | null {
  const known = values.filter((value): value is number => value != null && Number.isFinite(value));
  return known.length > 0 ? known.reduce((total, value) => total + value, 0) : null;
}

/**
 * Aportación de cada proveedor a un vehículo compartido (HU8): unidades, volumen, peso e importe,
 * con el importe mínimo de cada proveedor cuando se conoce.
 */
export function SupplyVehicleSuppliersPanel({ rows, t }: SupplyVehicleSuppliersPanelProps) {
  if (rows.length < 2) return null;
  const hasAfter = rows.some((row) => row.volumePctAfter != null || row.amountAfter != null || row.unitsAfter != null);
  const showMinimum = rows.some((row) => row.minAmount != null);
  const total = (pick: (row: SupplierContributionRow) => number | null | undefined) => sumKnown(rows.map(pick));
  const cellClass = 'px-2 py-1.5 text-right tabular-nums';
  return (
    <section aria-label={t('supply.supplyAgent.vehicleSuppliersTitle')} className="mb-3 rounded-lg border border-slate-200 bg-white p-3">
      <h3 className="text-sm font-semibold text-slate-900">{t('supply.supplyAgent.vehicleSuppliersTitle')}</h3>
      <p className="mt-0.5 text-[11px] text-slate-500">{t('supply.supplyAgent.vehicleSuppliersHint')}</p>
      <div className="mt-2 overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b border-slate-200 text-[10px] font-semibold uppercase tracking-wide text-slate-500">
              <th className="px-2 py-1.5 text-left">{t('supply.supplyAgent.vehicleSupplierColumn')}</th>
              <th className={cellClass}>{t('supply.supplyAgent.loadUnitsColumn')}</th>
              <th className={cellClass}>{t('supply.supplyAgent.vehicleVolumeColumn')}</th>
              <th className={cellClass}>{t('supply.supplyAgent.vehicleWeightColumn')}</th>
              <th className={cellClass}>{t('supply.supplyAgent.vehicleAmountColumn')}</th>
              {showMinimum ? <th className={cellClass}>{t('supply.supplyAgent.vehicleMinimumColumn')}</th> : null}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.supplierId ?? row.name} className="border-b border-slate-100 last:border-b-0">
                <td className="max-w-[220px] truncate px-2 py-1.5 font-medium text-slate-800" title={row.name}>{row.name}</td>
                <td className={cellClass}>{change(formatNumber(row.units), row.unitsAfter == null ? null : formatNumber(row.unitsAfter))}</td>
                <td className={cellClass}>{change(formatPct(row.volumePct), row.volumePctAfter == null ? null : formatPct(row.volumePctAfter))}</td>
                <td className={cellClass}>{change(formatPct(row.weightPct), row.weightPctAfter == null ? null : formatPct(row.weightPctAfter))}</td>
                <td className={cellClass}>{change(formatMoney(row.amount), row.amountAfter == null ? null : formatMoney(row.amountAfter))}</td>
                {showMinimum ? (
                  <td className={cellClass}>
                    {row.minAmount == null ? <span className="text-slate-300">—</span> : (
                      <span className="inline-flex flex-wrap items-center justify-end gap-1">
                        <span className="text-slate-600">{formatMoney(row.minAmount)}</span>
                        {row.minMet == null ? null : (
                          <span className={`rounded-full px-1.5 py-0.5 text-[10px] font-semibold ${row.minMet ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-800'}`}>
                            {t(row.minMet ? 'supply.supplyAgent.vehicleMinimumMet' : 'supply.supplyAgent.vehicleMinimumUnmet')}
                          </span>
                        )}
                      </span>
                    )}
                  </td>
                ) : null}
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="border-t border-slate-200 font-semibold text-slate-900">
              <td className="px-2 py-1.5 text-left">{t('supply.supplyAgent.vehicleTotalRow')}</td>
              <td className={cellClass}>{change(formatNumber(total((row) => row.units)), hasAfter ? formatNumber(total((row) => row.unitsAfter ?? row.units)) : null)}</td>
              <td className={cellClass}>{change(formatPct(total((row) => row.volumePct)), hasAfter ? formatPct(total((row) => row.volumePctAfter ?? row.volumePct)) : null)}</td>
              <td className={cellClass}>{change(formatPct(total((row) => row.weightPct)), hasAfter ? formatPct(total((row) => row.weightPctAfter ?? row.weightPct)) : null)}</td>
              <td className={cellClass}>{change(formatMoney(total((row) => row.amount)), hasAfter ? formatMoney(total((row) => row.amountAfter ?? row.amount)) : null)}</td>
              {showMinimum ? <td /> : null}
            </tr>
          </tfoot>
        </table>
      </div>
    </section>
  );
}
